import React, { useState, useEffect } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  Hash,
  Calendar,
  X,
  ZoomIn,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/Badge';
import { Customer } from '../../types/customer';
import { formatCNPJ } from '../../utils/cnpjValidator';
import { makeAbsolutePhotoUrl } from '../../services/api';
import { useKeycloak } from '../../contexts/KeycloakContext';
import { useUserProfilePicture } from '../../hooks/useUserProfilePicture';

interface CustomerInfoHeaderProps {
  customer: Customer;
  className?: string;
}

const typeGradient: Record<string, string> = {
  condominio: 'from-blue-50 to-indigo-50 border-blue-100',
  empresa:    'from-green-50 to-emerald-50 border-green-100',
  fabrica:    'from-orange-50 to-amber-50 border-orange-100',
};

const typeAvatarColor: Record<string, string> = {
  condominio: 'bg-blue-600 text-white',
  empresa:    'bg-green-600 text-white',
  fabrica:    'bg-orange-600 text-white',
};

const typeLabel: Record<string, string> = {
  condominio: 'Condomínio',
  empresa:    'Empresa',
  fabrica:    'Fábrica',
};

const condoTypeLabel: Record<string, string> = {
  casa:        'Condomínio de Casas',
  apartamento: 'Condomínio de Apartamentos',
  misto:       'Condomínio Misto',
};

const statusConfig: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
  active:    { variant: 'success', label: 'Ativo' },
  inactive:  { variant: 'warning', label: 'Inativo' },
  suspended: { variant: 'danger',  label: 'Suspenso' },
};

const getInitials = (name: string): string =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

export const CustomerInfoHeader: React.FC<CustomerInfoHeaderProps> = ({ customer, className }) => {
  const [logoZoomed, setLogoZoomed] = useState(false);
  const navigate = useNavigate();
  const { user } = useKeycloak();
  const { profilePicture } = useUserProfilePicture();

  useEffect(() => {
    if (!logoZoomed) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setLogoZoomed(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [logoZoomed]);

  if (!customer) return null;

  const logoSrc = customer.photo_url
    ? makeAbsolutePhotoUrl(customer.photo_url)
    : customer.logo ?? null;

  const formatDate = (date: string | undefined): string => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  const gradient = typeGradient[customer.type] ?? 'from-gray-50 to-gray-100 border-gray-200';
  const avatarColor = typeAvatarColor[customer.type] ?? 'bg-gray-600 text-white';
  const statusInfo = customer.status ? statusConfig[customer.status] : null;
  const hasAddress = !!(customer.address || customer.city || customer.state);
  const condoType = (customer as any).condoType as string | undefined;
  const subLabel = condoType && condoTypeLabel[condoType]
    ? condoTypeLabel[condoType]
    : typeLabel[customer.type] ?? customer.type;

  return (
    <>
      {/* Logo lightbox */}
      {logoZoomed && logoSrc && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLogoZoomed(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            onClick={() => setLogoZoomed(false)}
          >
            <X className="h-7 w-7" />
          </button>
          <div
            className="flex flex-col items-center gap-4"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={logoSrc}
              alt={customer.name}
              className="max-w-xs max-h-72 rounded-2xl shadow-2xl object-contain"
            />
            <div className="text-center">
              <p className="text-white font-semibold text-lg leading-tight">{customer.name}</p>
              <p className="text-white/50 text-sm mt-0.5">{subLabel}</p>
            </div>
          </div>
        </div>
      )}

    <div className={`rounded-2xl overflow-hidden shadow-sm border mb-6 ${className || ''}`}>
      {/* Hero band */}
      <div className={`bg-gradient-to-r ${gradient} border-b border-gray-200/60 p-6`}>
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div
              className={`w-20 h-20 rounded-2xl shadow-sm overflow-hidden ${logoSrc ? 'cursor-zoom-in group relative' : ''}`}
              onClick={() => logoSrc && setLogoZoomed(true)}
            >
              {logoSrc ? (
                <>
                  <img
                    src={logoSrc}
                    alt={customer.name}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                    <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow" />
                  </div>
                </>
              ) : (
                <div className={`w-full h-full flex items-center justify-center rounded-2xl ${avatarColor}`}>
                  <span className="text-2xl font-bold">{getInitials(customer.name)}</span>
                </div>
              )}
            </div>
            {logoSrc && (
              <span className="text-[10px] font-medium text-gray-400 tracking-wide uppercase">Logo</span>
            )}
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold text-gray-900 leading-tight">{customer.name}</h2>
              {statusInfo && (
                <Badge variant={statusInfo.variant} size="sm" dot className="flex-shrink-0 mt-1">
                  {statusInfo.label}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{subLabel}</p>
            {customer.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{customer.description}</p>
            )}
          </div>

          {/* Logged-in user pill */}
          {user && (
            <button
              onClick={() => navigate('/profile/user')}
              className="flex-shrink-0 flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/70 hover:bg-white border border-white/80 hover:border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 group"
              title="Meu Perfil"
            >
              <div className="relative flex-shrink-0">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt={user.name || 'Usuário'}
                    className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fb = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fb) fb.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center border-2 border-white shadow-sm"
                  style={{ display: profilePicture ? 'none' : 'flex' }}
                >
                  <span className="text-sm font-bold text-gray-600">
                    {user.name?.charAt(0).toUpperCase() ?? '?'}
                  </span>
                </div>
              </div>
              <div className="hidden sm:block text-left min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate leading-tight max-w-[120px]">
                  {user.name || user.username}
                </p>
                <p className="text-[10px] text-gray-400 leading-tight">Meu Perfil</p>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          )}
        </div>
      </div>

      {/* Chips strip — only show chips that have data, spread evenly with dividers */}
      {(() => {
        const cnpj = customer.postalCode || (customer as any).postal_code;
        const chips = [
          customer.email ? {
            key: 'email',
            icon: <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />,
            label: 'Email',
            content: <span className="text-sm font-medium text-gray-800 truncate">{customer.email}</span>,
          } : null,
          customer.phone ? {
            key: 'phone',
            icon: <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />,
            label: 'Telefone',
            content: <span className="text-sm font-medium text-gray-800">{customer.phone}</span>,
          } : null,
          hasAddress ? {
            key: 'address',
            icon: <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />,
            label: 'Endereço',
            content: (
              <>
                {customer.address && <span className="text-sm font-medium text-gray-800 block truncate">{customer.address}</span>}
                {(customer.city || customer.state) && (
                  <span className="text-xs text-gray-500">{[customer.city, customer.state].filter(Boolean).join(' · ')}</span>
                )}
              </>
            ),
          } : null,
          cnpj ? {
            key: 'cnpj',
            icon: <Hash className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />,
            label: 'CNPJ',
            content: <span className="text-sm font-medium text-gray-800">{formatCNPJ(cnpj)}</span>,
          } : null,
          customer.created_at ? {
            key: 'date',
            icon: <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />,
            label: 'Cadastro',
            content: <span className="text-sm font-medium text-gray-800">{formatDate(customer.created_at)}</span>,
          } : null,
        ].filter(Boolean) as NonNullable<typeof chips[0]>[];

        if (chips.length === 0) return null;

        return (
          <div className="bg-white flex divide-x divide-gray-100">
            {chips.map((chip) => (
              <div key={chip.key} className="flex-1 flex items-start gap-2 px-5 py-3 min-w-0">
                {chip.icon}
                <div className="min-w-0 flex-1">
                  <span className="text-xs text-gray-400 block leading-none mb-0.5">{chip.label}</span>
                  {chip.content}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
    </>
  );
};
